import { getFeatureSwitches } from "../api/featureSwitches";
import { AppMetadata } from "../appBridge/AppMetadata";
import { StudentManager } from "../controllers/StudentManager";
import { TeacherManager } from "../controllers/TeacherManager";
import { UserManager } from "../controllers/UserManager";
import localStorage from "../utils/localStorage";
import { FeatureSwitchQueryBuilder } from "./FeatureSwitchQuery";
import { FeatureSwitchesResponse } from "./IFeatureSwitchManager";

const FEATURE_SET_ID_LOCALSTORAGE_KEY = "monsterWorld/featureSwitches/featureSetId";

export type FeatureSwitchChecker = (fsName: string, variant: string) => boolean;
export class FeatureSwitchManager {
  static featureSwitches: Record<string, string> = {};
  static appMetadata: AppMetadata = {};

  static reset() {
    FeatureSwitchManager.featureSwitches = {};
  }

  static async fetchFeatureSwitches() {
    const user = UserManager.instance.user;
    if (!user) {
      console.error("Error fetching feature switches when not signed in.");
      return;
    }

    const entityId = UserManager.instance.getEntityId() || "";

    const schoolId = (user.type === "teacher" ? user.teacher.schoolId : user.student.schoolIds?.[0]) || "";
    const classes = user.type === "teacher" ? TeacherManager.instance.classes : StudentManager.instance.classes;
    const classroomIds = classes.flatMap((c) => c._id);

    // get local value for featureSetId to be used on API request caching management
    const featureSetIdKey = `monsterverse/${FEATURE_SET_ID_LOCALSTORAGE_KEY}/${entityId}`;
    const featureSetId = localStorage.getItem(featureSetIdKey) || "";

    const query = FeatureSwitchQueryBuilder.buildQuery(featureSetId, entityId, schoolId, classroomIds);
    const data = await getFeatureSwitches(query);

    // update local value for featureSetId to be used on future requests
    localStorage.setItem(featureSetIdKey, data.featureSetId);

    FeatureSwitchManager.featureSwitches = FeatureSwitchManager.extractFeatureSwitches(data);
  }

  static extractFeatureSwitches(response: FeatureSwitchesResponse): Record<string, string> {
    const classroomKeys = Object.keys(response).filter((k) => /class-.*/g.test(k));
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const classroomFSs = classroomKeys.map((k: any) => response[k]);

    // merge feature switch variants across classrooms
    const mergedClassroomFSs = classroomFSs.reduce((acc, cur) => {
      Object.entries(cur).forEach(([key, value]) => {
        // discard off and control variants in favor of more permissive values
        if (!acc[key] || (value != "off" && value != "control")) {
          acc[key] = value;
        }
      });
      return acc;
    }, {});
    return { ...response.school, ...response.user, ...mergedClassroomFSs };
  }

  static isFeatureSwitchVariant(fsName: string, variant: string): boolean {
    const isInVariant = FeatureSwitchManager.featureSwitches[fsName] === variant;
    return isInVariant && FeatureSwitchManager.isValidFeatureSwitchForCurrentPlatform(fsName);
  }

  static getValidFeatureSwitches(variant: string) {
    const validFeatureSwitches = new Array<string>();
    for (const fsName in FeatureSwitchManager.featureSwitches) {
      const isInVariant = FeatureSwitchManager.featureSwitches[fsName] === variant;
      if (isInVariant && FeatureSwitchManager.isValidFeatureSwitchForCurrentPlatform(fsName)) {
        validFeatureSwitches.push(fsName);
      }
    }
    return validFeatureSwitches;
  }

  static isValidFeatureSwitchForCurrentPlatform(fsName: string): boolean {
    const platform = FeatureSwitchManager.appMetadata.platform?.toLowerCase() || "web";
    const name = fsName.toLowerCase();
    const fsPrefixesByPlatorm: Record<string, string> = {
      ios: "ios_",
      android: "android_",
      android_web: "android_",
      web: "web",
    };
    const platformSpecificFSPrefixes = Object.values(fsPrefixesByPlatorm);
    const isFeatureSwitchPlatormSpecific = platformSpecificFSPrefixes.some((prefix) => name.startsWith(prefix));
    return !isFeatureSwitchPlatormSpecific || name.startsWith(fsPrefixesByPlatorm[platform]);
  }
}
